import { designVariables } from "@studio3t/design-variables";
import {
  type AlertActionProps,
  type AlertState,
  GenericAlert,
  MicroButton,
} from "@studio3t/generic-components";
import {
  AnimatePresence,
  motion,
  type TargetAndTransition,
  type Transition,
  useReducedMotion,
} from "motion/react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { type DefaultTheme } from "styled-components";

// Toast notifications: a stack in the bottom-right corner, the newest in
// front, the older ones tucked behind it — smaller, higher and fainter with
// every step back (after motion.dev's toast stack). Hovering the stack fans
// it out into a list so every message can be read and closed; leaving
// tucks it back. Each toast is the design-system alert at the Figma toast
// metrics (ui.toast: padding 20, corner 10) with a small close in its
// corner; info and confirm toasts leave on their own, warnings later,
// errors stay until closed.

const TOAST = designVariables.ui.toast;
const SEMANTIC = designVariables.ui.semantic;

export type ToastState = AlertState;

export interface ToastInput {
  state: ToastState;
  message: ReactNode;
  /** Buttons inside the toast (the alert's own action row). */
  actions?: AlertActionProps["actions"];
  /** How long it stays, ms. 0 keeps it until closed. Default by state. */
  duration?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

const DEFAULT_DURATION: Record<ToastState, number> = {
  Info: 5000,
  Confirm: 5000,
  Warning: 8000,
  Error: 0,
};

// How many stay visible when tucked; the rest hide behind them (the
// counter keeps them reachable on hover).
const VISIBLE_TUCKED = 4;
// Per step back: 10px up, 6% smaller, 20% fainter (motion.dev's stack).
const STEP = { y: 10, scale: 0.06, opacity: 0.2 };
// Enter rises 60px from 85% size, older toasts shuffle back 50ms apart;
// leaving drops 20px to 80% on a short tween.
const ENTER = { y: 60, scale: 0.85 };
const EXIT = { y: 20, scale: 0.8 };
const STAGGER = 0.05;
// The travel spring bounces a touch (~3% overshoot); opacity rides beside
// it as a linear fade so both land together instead of the fade popping
// ahead of the movement.
const TRAVEL: Transition = { type: "spring", stiffness: 360, damping: 28 };
// The box, though, settles without overshoot (critical damping at this
// stiffness): a card that overshoots its own height shows a sliver of
// itself under the alert's rounded bottom, like a second edge.
const RESIZE: Transition = { type: "spring", stiffness: 360, damping: 38 };
const FADE: Transition = { type: "tween", duration: 0.3, ease: "linear" };
const LEAVE: Transition = { type: "tween", duration: 0.2, ease: "easeIn" };

interface ToastContextValue {
  /** Shows a toast; returns its id for an early dismiss. */
  toast: (input: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast requires ToastProvider");
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  // The queue's source of truth, mirrored into state: `toast` needs the
  // current queue synchronously to hand a duplicate caller the id it will
  // later dismiss, and two calls in one tick must see each other.
  const queue = useRef<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    queue.current = queue.current.filter((t) => t.id !== id);
    setToasts(queue.current);
  }, []);
  // One toast per distinct message: a failure that several parts of the
  // app report at once (both chat providers losing the same request, say)
  // reads as one error, not a stack of copies. The duplicate caller gets
  // the shown toast's id, so its dismiss retracts that toast like any
  // other. Node messages are never merged, since they cannot be compared.
  const toast = useCallback((input: ToastInput) => {
    const same =
      typeof input.message === "string"
        ? queue.current.find(
            (t) => t.state === input.state && t.message === input.message,
          )
        : undefined;
    if (same) return same.id;
    const item = { ...input, id: nextId.current++ };
    queue.current = [...queue.current, item];
    setToasts(queue.current);
    return item.id;
  }, []);

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <ToastStack toasts={toasts} onDismiss={dismiss} />,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

// The tucked stack's transform per depth (0 = the newest, in front).
function tucked(depth: number) {
  const hidden = depth >= VISIBLE_TUCKED;
  return {
    y: -depth * STEP.y,
    scale: 1 - depth * STEP.scale,
    opacity: hidden ? 0 : 1 - depth * STEP.opacity,
  };
}

// Where a card sits in the stack: its depth, how far it rises when fanned
// out, whether the stack is fanned out, and the box it shows.
interface Placement {
  depth: number;
  lift: number;
  expanded: boolean;
  height: number | undefined;
}

// The card's motion for its placement. Tucked, the resting offsets are the
// stack; fanned out, each card rises by `lift` to sit above the ones in
// front of it. `height` is the box the card shows — the front card's while
// tucked, its own when fanned out — animated as a plain value, so the
// content is cropped by the moving edge, never scaled with it. `calm`
// (prefers-reduced-motion) keeps the resting offsets — they are the
// layout — but enters and leaves by fading alone, with no shuffle delay.
function toastMotion(
  { depth, lift, expanded, height }: Placement,
  calm: boolean,
) {
  const rest = expanded ? { y: -lift, scale: 1, opacity: 1 } : tucked(depth);
  const initial: TargetAndTransition = calm
    ? { opacity: 0, y: rest.y, scale: rest.scale }
    : { opacity: 0, y: ENTER.y, scale: ENTER.scale };
  const exit: TargetAndTransition = calm
    ? { opacity: 0, transition: LEAVE }
    : { opacity: 0, y: EXIT.y, scale: EXIT.scale, transition: LEAVE };
  const delay = calm ? 0 : depth * STAGGER;
  const transition: Transition = {
    ...TRAVEL,
    opacity: FADE,
    height: { ...RESIZE, delay },
    delay,
  };
  return {
    initial,
    animate: { ...rest, height: height ?? "auto", zIndex: 100 - depth },
    exit,
    transition,
  };
}

function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  // Each toast's natural height, measured from its body: the card itself
  // may be showing another toast's box. A toast that has left takes its
  // entry with it (`undefined`), so the map holds only what is on screen.
  const [heights, setHeights] = useState<Record<number, number>>({});
  const measure = useCallback((id: number, height: number | undefined) => {
    setHeights((prev) => {
      if (prev[id] === height) return prev;
      const next = { ...prev };
      if (height === undefined) delete next[id];
      else next[id] = height;
      return next;
    });
  }, []);
  // Oldest first in the DOM, so fanned out the newest is at the bottom,
  // nearest the corner — where it already sits tucked (z-index puts it in
  // front there; depth counts back from it).
  const last = toasts.length - 1;
  if (toasts.length === 0) return null;
  const front = heights[toasts[last].id];
  // Fanned out, a card rises by the heights of the cards in front of it
  // plus the gaps between them.
  const lifts: number[] = [];
  let reach = 0;
  for (let depth = 0; depth <= last; depth++) {
    lifts[depth] = reach;
    reach += (heights[toasts[last - depth].id] ?? 0) + TOAST.gaph;
  }
  reach -= TOAST.gaph;
  return (
    <Stack
      // Fanned out, the stack grows upward to cover the cards it has
      // lifted, gaps included, so the pointer never leaves it between two.
      style={{ minHeight: expanded ? reach : undefined }}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      aria-live="polite"
      data-testid="toast-stack"
    >
      {/* No `initial={false}`: the stack unmounts when empty, so the first
          toast mounts together with the presence wrapper, and that flag
          would skip its entrance — the very toast that has just arrived. */}
      <AnimatePresence>
        {toasts.map((item, index) => {
          const depth = last - index;
          return (
            <Toast
              key={item.id}
              item={item}
              depth={depth}
              lift={lifts[depth]}
              expanded={expanded}
              height={expanded ? heights[item.id] : front}
              onMeasure={measure}
              onDismiss={() => onDismiss(item.id)}
            />
          );
        })}
      </AnimatePresence>
    </Stack>
  );
}

function Toast({
  item,
  depth,
  lift,
  expanded,
  height,
  onMeasure,
  onDismiss,
}: {
  item: ToastItem;
  depth: number;
  lift: number;
  expanded: boolean;
  height: number | undefined;
  onMeasure: (id: number, height: number | undefined) => void;
  onDismiss: () => void;
}) {
  const duration = item.duration ?? DEFAULT_DURATION[item.state];
  const calm = useReducedMotion() === true;
  // The timer restarts in full after a hover — a toast never leaves while
  // it is being read.
  useEffect(() => {
    if (duration === 0 || expanded) return;
    const timer = setTimeout(onDismiss, duration);
    return () => clearTimeout(timer);
  }, [duration, expanded, onDismiss]);

  // The body is the toast at its natural height whatever box the card
  // shows; its size is the stack's measure of this toast. The observer's
  // own rect is the one to read: it is fractional where offsetHeight
  // rounds (a rounded-up height leaves a hairline of card under the
  // alert), and untransformed where a bounding rect carries the card's
  // tucked scale. It reports once on observe, so no first read is needed.
  const bodyRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const observer = new ResizeObserver(([entry]) => {
      onMeasure(item.id, entry.contentRect.height);
    });
    observer.observe(body);
    return () => {
      observer.disconnect();
      onMeasure(item.id, undefined);
    };
  }, [item.id, onMeasure]);

  return (
    <Card
      {...toastMotion({ depth, lift, expanded, height }, calm)}
      $state={item.state}
      data-testid="toast"
      data-state={item.state}
    >
      <Body ref={bodyRef}>
        <ToastAlert
          state={item.state}
          icon
          dismissible={false}
          message={item.message}
          actionProps={
            item.actions
              ? { position: "Bottom", actions: item.actions }
              : undefined
          }
        />
        <CloseButton onClick={onDismiss} aria-label="Close notification" />
      </Body>
    </Card>
  );
}

// Fixed to the corner, above modals (their backdrop sits at 1000). Every
// card shares the one grid cell, bottom-aligned, and the transforms tell
// them apart: tucked, the stepped-up offsets; fanned out, each lifted
// above the cards in front of it.
const Stack = styled.div`
  position: fixed;
  right: ${TOAST.padding}px;
  bottom: ${TOAST.padding}px;
  z-index: 1100;
  display: grid;
  align-items: end;
  width: 360px;
  max-width: calc(100vw - ${2 * TOAST.padding}px);

  & > * {
    grid-area: 1 / 1;
  }
`;

// The alert's own fill for a state (GenericAlert's rule), at rest or
// hovered: a translucent tint the alert lays over whatever is behind it.
function alertTint(theme: DefaultTheme, state: ToastState, hover: boolean) {
  if (state === "Error" && !hover) {
    return designVariables.primitives.supportive.error.alpha;
  }
  const key = `alert${state}Background${hover ? "Hover" : ""}`;
  return (theme as unknown as Record<string, string>)[key];
}

// Figma elevation/outset2 (the dropdown menu's shadow). The card clips:
// tucked, it shows the front card's box, and the body inside is cropped
// to it; while the box animates, the body is cropped to the moving edge.
// The card wears the alert's tint itself (over the app background, as the
// alert would lay it) and the alert goes clear, so a box taller than its
// alert — a short toast tucked behind a tall one, mid-move — is tinted
// edge to edge rather than showing bare card under the alert.
const Card = styled(motion.div)<{ $state: ToastState }>`
  position: relative;
  overflow: hidden;
  transform-origin: bottom center;
  border-radius: ${TOAST.corner}px;
  box-shadow:
    0 0 1px rgba(17, 17, 17, 0.05),
    0 2px 9px rgba(17, 17, 17, 0.07),
    0 1px 1px rgba(17, 17, 17, 0.06);
  background: ${(p) => tinted(alertTint(p.theme, p.$state, false), p.theme)};

  &:hover {
    background: ${(p) => tinted(alertTint(p.theme, p.$state, true), p.theme)};
  }
`;

// A translucent tint laid over the app background, as one opaque fill.
function tinted(tint: string, theme: DefaultTheme) {
  return `linear-gradient(${tint}, ${tint}), ${theme.semanticAppBackground}`;
}

// The toast's content at its natural height, whatever box the card has.
const Body = styled.div`
  position: relative;
`;

// The alert at the toast's metrics, with room on the right for the close.
// Clear: the card behind it carries its tint (see Card).
const ToastAlert = styled(GenericAlert)`
  width: 100%;
  background-color: transparent;

  /* The icon marks the first line, not the block: a long message reads
     from its top. The row keeps the icon's height, and the text drops
     by half the difference so its first line is centred on the icon
     (normal line-height at the alert's 12px is ~14.5px; the icon 20px). */
  & > div > div {
    align-items: flex-start;
  }

  & > div > div > span {
    margin-top: 3px;
  }

  &:hover {
    background-color: transparent;
  }

  padding: ${TOAST.padding}px;
  padding-right: ${TOAST.padding + 14 + SEMANTIC.spacings}px;
  border-radius: ${TOAST.corner}px;
`;

const CloseButton = styled(MicroButton)`
  position: absolute;
  top: ${TOAST.padding - 4}px;
  right: ${TOAST.padding - 4}px;
`;
